<script setup lang="ts">
import {formatting, LegacyOrder, legacyOrderKey} from '@hantera/portal-app';
import {InlineInput} from '@hantera/design-system';
import {inject, onMounted, onUnmounted, ref, watch} from 'vue';
import useBackend from '@/composition/useBackend';
import {CatalogSearchHit} from '@/backend/catalog';

const props = defineProps<{
  canEdit: boolean
  productNumber?: string
  inventoryIds?: string[]
}>();

const emit = defineEmits<{
  (event: 'selected', value: CatalogSearchHit | undefined): void
}>();

const isOpen = ref(false);
const inputElem = ref();
const containerElem = ref<HTMLElement>();
const backend = useBackend();
const { currency } = formatting;
const order = inject(legacyOrderKey, ref<LegacyOrder>(<any>{}));
let currentSearch = 0;
const searchHits = ref<CatalogSearchHit[]>();
const selectedIdx = ref(0);
const hitsContainerStyle = ref<any>({});

const inputProductNumber = ref(props.productNumber);
watch(() => props.productNumber, (val) => {
  inputProductNumber.value = props.productNumber;
});

let inputDebouncer: ReturnType<typeof setTimeout>;
watch(inputProductNumber, () => {
  if (inputDebouncer) {
    clearTimeout(inputDebouncer);
  }
  inputDebouncer = setTimeout(async () => {
    if (!inputProductNumber.value) { return; }
    let i = ++currentSearch;
    const result = await backend.catalog.search(
      inputProductNumber.value,
      order.value.currencyCode,
      order.value.countryCode,
      order.value.language,
      order.value.channelId,
      order.value.customer.customerNumber,
      order.value.includeVat,
      props.inventoryIds
    );

    // Prevent getting result from parallell previous search
    if (currentSearch === i) {
      searchHits.value = result;
      selectedIdx.value = 0;
    }
  }, 200);
});

watch(isOpen, () => {
  if (isOpen && containerElem.value) {
    const rect = containerElem.value.getBoundingClientRect();
    const width = document.documentElement.clientWidth;
    const height = document.documentElement.clientHeight;
    hitsContainerStyle.value = {
      top: `${rect.top + rect.height}px`,
      left: `${rect.left}px`,
      maxWidth: `${width - rect.left}px`,
      maxHeight: `${height - rect.top - rect.height}px`,
    };
  }
})

onMounted(() => {
  window.document.addEventListener('keydown', handleKey);
});

onUnmounted(() => {
  window.document.removeEventListener('click', close);
  window.document.removeEventListener('keydown', handleKey);
});

function openSearch() {
  inputElem.value.select();
  isOpen.value = true;
  window.document.addEventListener('click', close);
}

function close() {
  if (isOpen.value) {
    inputProductNumber.value = props.productNumber;
    isOpen.value = false;
    window.document.removeEventListener('click', close);
  }
}

function selectIdx(idx: number) {
  selectedIdx.value = idx;
}

function handleKey(e: KeyboardEvent) {
  if (isOpen.value && searchHits.value) {
    if (e.key === 'ArrowDown' && selectedIdx.value < searchHits.value.length - 1) {
      selectedIdx.value += 1;
    }
    if (e.key === 'ArrowUp' && selectedIdx.value > 0) {
      selectedIdx.value -= 1;
    }
    if (e.key === 'Enter') {
      select();
    }
    if (e.key === 'Escape') {
      close();
    }
  }
}

function select() {
  const selected = searchHits.value?.at(selectedIdx.value);
  if (selected) {
    isOpen.value = false;
    inputProductNumber.value = selected.productNumber;
    window.document.removeEventListener('click', close);
    emit('selected', selected);
  }
}

function blur() {
  if (!inputProductNumber.value) {
    isOpen.value = false;
    window.document.removeEventListener('click', close);
    emit('selected', undefined);
  }
}

</script>

<template>
  <div
    class="product-selector"
    ref="containerElem"
  >
    <InlineInput
      v-if="canEdit"
      v-model="inputProductNumber"
      placeholder="Select product..."
      @click.stop
      @focus="openSearch"
      @blur="blur"
      class="product-selector__input"
      ref="inputElem"
    />
    <span
      v-if="!canEdit"
      class="product-selector__item-number"
    >
      {{ productNumber }}
    </span>
  </div>
  <Teleport to="body">
    <Transition
      name="product-selector__transition"
      :duration="100"
    >
      <div
        v-show="isOpen"
        class="product-selector__hits-container neat-scroll"
        :style="hitsContainerStyle"
        @click.stop
      >
        <div
          v-if="!searchHits"
          class="product-selector__hits-placeholder"
        >Type product number to search</div>
        <div
          class="product-selector__hits"
          v-if="searchHits"
        >
          <div
            v-for="(hit, idx) in searchHits"
            :key="hit.productNumber"
            class="product-selector__hit"
            :class="{
              'product-selector__hit--selected': selectedIdx === idx
            }"
            @mouseover="selectIdx(idx)"
            @click="select"
          >
            <div class="product-selector__image">
              <img :src="hit.productImage" />
            </div>
            <div class="product-selector__number">
              {{ hit.productNumber }}
            </div>
            <div class="product-selector__price">
              {{ currency(hit.unitPrice, order.currencyCode) }}
            </div>
            <div class="product-selector__name">
              {{ hit.productName }}
            </div>
            <div class="product-selector__stock">
              {{ hit.availableStock }} pcs
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.product-selector {
  display: flex;
  font-weight: 600;
  align-items: center;
}
</style>

<style>

.product-selector__hits-container {
  position: absolute;
  top: -9999px;
  left: -9999px;
  overflow: overlay;

  border-radius: 4px;
  border: 1px solid hsl(var(--color-80));
  background-color: hsl(var(--color-100));
  color: rgb(var(--color-invert));
  box-shadow: 1px 1px 4px #0006, 0 0 3px #0003;
}

.product-selector__hits-placeholder {
  padding: 10px;
}

.product-selector__hits {
  display: grid;
  grid-template-columns: 50px auto min-content;
  grid-auto-rows: 25px;
}

.product-selector__hit {
  display: contents;
  cursor: pointer;
}

.product-selector__hit--selected div {
  background-color: hsl(var(--color-80));
}

.product-selector__image {
  grid-column: 1 / 2;
  grid-row: auto / span 2;
  padding: 5px;
}

.product-selector__image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.product-selector__input .inline-input__input {
  font-weight: bold;
}

.product-selector__number {
  grid-column: 2 / 3;
  padding-top: 5px;
  padding-right: 5px;
}

.product-selector__price {
  padding-top: 5px;
  grid-column: 3 / 4;
  padding-right: 5px;
  text-align: right;
}

.product-selector__stock {
  grid-column: 3 / 4;
  padding-right: 5px;
  text-align: right;
}

.product-selector__name {
  grid-column: 2 / 3;
  padding-right: 5px;
}
</style>